import { SITE_CONFIG } from '../config/site-config';
import { EXTERNAL_LINK_ATTRS, isConfigured } from '../lib/links';
import { icon } from './icons';

export type SocialNetwork = 'linkedin' | 'medium' | 'youtube' | 'instagram';

interface SocialProfile {
  readonly name: string;
  readonly url: () => string;
  readonly aria: string;
  readonly cta: string;
  readonly desc: string;
  readonly note?: string;
}

/** Enterprise priority order: LinkedIn → Medium → YouTube → Instagram. */
const ALL_NETWORKS: readonly SocialNetwork[] = ['linkedin', 'medium', 'youtube', 'instagram'];

export const SOCIAL_PROFILES: Readonly<Record<SocialNetwork, SocialProfile>> = {
  linkedin: {
    name: 'LinkedIn',
    url: () => SITE_CONFIG.LINKEDIN_URL,
    aria: 'ReTax360 on LinkedIn',
    cta: 'Follow ReTax360 on LinkedIn',
    desc: 'Company updates &amp; enterprise perspectives',
  },
  medium: {
    name: 'Medium',
    url: () => SITE_CONFIG.MEDIUM_URL,
    aria: 'Asumani Sula on Medium',
    cta: 'Read Asumani Sula on Medium',
    desc: 'Long-form thinking on AI, orchestration and enterprise workflows',
    note: 'Founder perspectives from Asumani Sula',
  },
  youtube: {
    name: 'YouTube',
    url: () => SITE_CONFIG.YOUTUBE_URL,
    aria: 'ReTax360 on YouTube',
    cta: 'Watch ReTax360 on YouTube',
    desc: 'Product demonstrations, conversations and videos',
  },
  instagram: {
    name: 'Instagram',
    url: () => SITE_CONFIG.INSTAGRAM_URL,
    aria: 'ReTax360 on Instagram',
    cta: 'Follow ReTax360 on Instagram',
    desc: 'ReTax360 updates and behind-the-scenes content',
  },
};

/** The networks (in priority order) that have a URL configured. */
export const configuredNetworks = (networks: readonly SocialNetwork[] = ALL_NETWORKS): SocialNetwork[] =>
  networks.filter((network) => isConfigured(SOCIAL_PROFILES[network].url()));

export const configuredSocialUrls = (networks?: readonly SocialNetwork[]): string[] =>
  configuredNetworks(networks).map((network) => SOCIAL_PROFILES[network].url());

/** Icon + text label link to a social profile. */
export function socialLink(
  network: SocialNetwork,
  { label, className = 'soc' }: { label?: string; className?: string } = {},
): string {
  const profile = SOCIAL_PROFILES[network];
  return `<a class="${className}" href="${profile.url()}" ${EXTERNAL_LINK_ATTRS} aria-label="${profile.aria} (opens in a new tab)">${icon(network)}<span>${label ?? profile.name}</span></a>`;
}
